import { Close } from "@mui/icons-material";
import { Box, Dialog, DialogContent, IconButton, Tooltip, useMediaQuery } from "@mui/material";
import { makeStyles, useTheme } from "@mui/styles";
import { isEmpty } from "lodash";
import { memo, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import subjx from "subjx";

import "subjx/dist/style/subjx.css";

import { HOTSPOT_STATUS } from "@/constants/index.js";
import { setHotspotData, updateVisualBuilderData } from "@/store/actions";
import { currentElementSelector, hotspotDataSelector, hotspotsSelector, simpleValuesSelector } from "@/store/selectors";

import { useUrlParams } from "../../../hooks";
import { HotspotService } from "../../../services";
import { getMediaLink } from "../../../utils/mediaLink";
import objectDifference from "../../../utils/objectDifference";
import renderTypeIcon from "../../../utils/renderTypeIcon";
import useDebounce from "../../../utils/useDebounce";

const styles = makeStyles((theme) => {
  const getCursorType = (props) =>
    props.isPublicPage || props.popoverAction === "hotspot" ? "pointer" : "not-allowed";

  return {
    hotspot: {
      cursor: getCursorType,
      pointerEvents: "auto",
      fill: "transparent",
      "&:hover": {
        cursor: getCursorType,
        fill: (props) => props.isPublicPage && "rgba(0,0,0,0.5)",
      },
    },
    svgCanvas: {
      zIndex: (props) => props.open && 1300,
      display: "inline-block",
      position: "absolute",
      top: 0,
      left: 0,
      height: "auto",
      width: "100%",
      backgroundColor: "#fff",
    },
    tooltip: {
      backgroundColor: "#333",
      color: "white",
      fontSize: "12px ",
      lineHeight: "16px",
      borderRadius: "2px",
    },
    arrow: {
      color: "#333",
    },
    icon: {
      fill: "none",
      marginRight: "10px",
    },
    mapCanvas: {
      position: "relative",
      width: "100%",
      verticalAlign: "middle",
      margin: 0,
      overflow: "hidden",
    },
    map: {
      zIndex: -50,
      width: "100%",
      height: "100%",
      objectFit: "contain",
      visibility: "hidden",
    },
    realMap: {
      width: "100%",
      height: "100%",
      objectFit: "contain",
    },
    activeHover: {
      fill: "rgba(0,0,0,0.5)",
    },
    active: {
      border: "1px dashed black",
    },
    popupClose: {
      display: "flex",
      color: "#fff",
      "& .MuiSvgIcon-root": {
        fontSize: "24px",
      },
      backgroundColor: "#222222",
      width: "100%",
      height: "32px",
      justifyContent: "flex-end",
    },
    closeModalButton: {
      "& *": {
        fontSize: "10px",
        display: "inline-block",
      },
      "&:hover": {
        cursor: "pointer",
      },
      color: "#fff",
      alignSelf: "flex-end",
    },
    paper: {
      maxWidth: false,
      boxShadow: theme.shadows[5],
      backgroundColor: "transparent",
      padding: "0",
      paddingTop: "0 !important",
      display: "flex",
      "@media (max-width:960px)": {
        width: "100vw",
      },
      "@media (max-width:650px) and (orientation: portrait)": {
        flexDirection: "column",
        justifyContent: "flex-start",
      },
    },
    dialogBackground: {
      backgroundColor: "transparent",
      "& .MuiDialog-paper": {
        backgroundColor: "transparent",
      },
    },
    mediaPreview: {
      padding: 0,
      margin: "0",
      width: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexDirection: "column",
      backgroundColor: "tra nsparent",
      "@media (max-width:960px)": {
        flex: "1",
      },
      "@media (max-width:650px) and (orientation: portrait)": {
        borderRight: "none",
        borderBottom: "1px solid #f5f5f5",
      },
    },
  };
});

function MapHotspotCanvas(props) {
  const { experienceId, propertyId } = props;

  const params = useUrlParams();
  const [hotSpotData, setHotSpotData] = useState({});
  const [initClick, setInitClick] = useState(false);
  const [image, setImage] = useState({});

  const [existingHotspot, setExistingHotspot] = useState(null);
  const debouncedHotSpotData = useDebounce(hotSpotData, 1500);

  const theme = useTheme();
  const fullScreenSmall = useMediaQuery(theme.breakpoints.down("lg"));

  const {
    element: { id: elementId },
  } = useSelector(currentElementSelector);
  const { hotspots } = useSelector(hotspotsSelector);

  const {
    hotspotMode = {},
    mapHotspotCanvas,
    hoveredHotspot,
    popoverAction,
    mapPreview,
  } = useSelector(hotspotDataSelector(propertyId));

  const { "public-page": isPublicPage = false } = useSelector(simpleValuesSelector);
  const { status, type } = hotspotMode;

  const [hotspotId, setHotspotId] = useState(null);

  const { open, url: mapUrl } = mapHotspotCanvas || {};

  const [isDone, setIsDone] = useState(false);
  const dispatch = useDispatch();

  const classes = styles({ open, popoverAction, isPublicPage });

  const hotspotsIds = hotspots.map((hotspot) => hotspot.id);

  const [currentHotspot, setCurrentHotspot] = useState(
    hotspots.filter((hotspot) => hotspot.id === (hotspotId || existingHotspot))[0] || null,
  );
  const draggables = useRef([]);

  useEffect(() => {
    if (
      debouncedHotSpotData &&
      (hotspotId || existingHotspot) &&
      propertyId &&
      experienceId &&
      elementId &&
      isDone &&
      !initClick &&
      Object.keys(debouncedHotSpotData).length > 0
    ) {
      (async () => {
        await HotspotService.updateHotspot(propertyId, experienceId, elementId, hotspotId || existingHotspot, {
          positionData: debouncedHotSpotData,
          status: HOTSPOT_STATUS.CREATED,
        });
      })();
      setHotSpotData({});
      dispatch(setHotspotData(propertyId, { hotspotMode: { status: false, type: null } }));
    }
  }, [debouncedHotSpotData, currentHotspot?.id, existingHotspot, hotspotId, isDone]);

  const svgParameters = () => {
    const onAction = async ({ transform }) => {
      const { a, b, c, d, e, f } = transform;
      const newTransform = {
        a,
        b,
        c,
        d,
        e,
        f,
      };

      // fires on resizing
      setHotSpotData((oldData) => {
        return { ...oldData, transform: newTransform };
      });
    };

    const methods = {
      onInit(el) {
        if (initClick) {
          const curHotspot = hotspots.find((ht) => ht.id === el[0].id);
          if (curHotspot?.status === HOTSPOT_STATUS.DRAFT) {
            return;
          }
          const activeHotspotCurrent = hotspots.find(
            (ht) =>
              ht.id ===
              (type === "new" ? ht.status === HOTSPOT_STATUS.DRAFT : el[0].id || hotspotId || existingHotspot),
          );
          setHotSpotData(activeHotspotCurrent?.positionData);
        } else {
          setHotSpotData({});
        }
        setHotspotId(el[0]?.id);

        draggables.current.forEach((otherDraggable) => {
          if (el[0]?.id !== otherDraggable.id) {
            otherDraggable?.object?.disable();
          }
        });
      },
      onMove: onAction,
      onResize: onAction,
      async onDrop({ clientX, clientY }) {
        // fires on drop
        const newData = {
          clientX,
          clientY,
        };

        setHotSpotData((oldData) => {
          return { ...oldData, ...newData };
        });
        setInitClick(false);
        if (existingHotspot) {
          draggables.current = draggables.current.filter((dragId) => {
            return dragId !== existingHotspot;
          });
        }
      },
      onDestroy() {
        setIsDone(true);
        draggables.current = [];
      },
    };
    return {
      container: "#svgCanvas",
      snap: {
        x: 5,
        y: 5,
        angle: 45,
      },
      cursorMove: "grab",
      cursorRotate: "crosshair",
      cursorResize: "nesw-resize",
      transformOrigin: true,
      scalable: true,
      rotatable: false,
      ...methods,
    };
  };
  useEffect(() => {
    if (isPublicPage && open) {
      dispatch(setHotspotData(propertyId, { fromHotspot: true }));
    }
    return () => {
      if (isPublicPage && !open) {
        dispatch(setHotspotData(propertyId, { fromHotspot: false }));
      }
    };
  }, [open]);

  const setAttributes = (element, attributes) => {
    Object.keys(attributes).forEach((attr) => {
      element.setAttribute(attr, attributes[attr]);
    });
  };

  const disableHotspot = (e) => {
    e?.stopPropagation();
    dispatch(setHotspotData(propertyId, { currentHotspot: null }));
    if (draggables.current.length > 0) {
      draggables?.current?.forEach((draggable) => {
        const { object } = draggable;
        const rect = object.elements[0];
        // rect.setAttribute('stroke-width', '2');
        rect.setAttribute("stroke-dasharray", "none");
        object?.disable();
      });
    }
  };

  const handleClose = async () => {
    const draftHotspots = hotspots?.filter((ht) => ht.status === HOTSPOT_STATUS.DRAFT);
    if (draftHotspots?.length > 0) {
      const promises = draftHotspots.map((dht) => {
        draggables.current = [];
        return HotspotService.deleteHotspot(propertyId, experienceId, elementId, dht.id);
      });

      await Promise.all(promises);
    }

    setExistingHotspot(null);
    setHotspotId(null);
    disableHotspot();
    dispatch(
      setHotspotData(propertyId, {
        mapHotspotCanvas: {
          elementId: null,
          url: null,
          open: !open,
        },
      }),
    );
  };

  const setControlCallback = (control) => {
    const attributes = {
      // 'stroke-width': 2,
      stroke: "#000",
      ...(control.nodeName === "line" && { "stroke-dasharray": "10 10" }),
    };
    setAttributes(control, attributes);
  };

  const activateHotspot = (e, id, linkedContent) => {
    setCurrentHotspot(hotspots.find((ht) => ht.id === id));
    if (isPublicPage) {
      const { id } = linkedContent || {};

      if (Object.keys(linkedContent || {}).length > 0) {
        dispatch(
          updateVisualBuilderData(params, {
            currentMediaItemId: id,
            search: {},
            typeFilterId: null,
            tagFilterId: null,
          }),
        );
        handleClose();
      }

      return;
    }
    if (popoverAction !== "hotspot") {
      return;
    }
    if (type !== "new") {
      dispatch(setHotspotData(propertyId, { currentHotspot: id }));
      e.stopPropagation();
      const dragmap = draggables.current.map((drag) => drag.id);
      if (!dragmap.includes(id)) {
        const xDraggable = subjx(`.hotspot-${id}`).drag(svgParameters());
        draggables.current.push({
          id,
          object: xDraggable,
        });
        const rect = xDraggable?.elements[0];
        rect?.setAttribute("style", "cursor: grab");
        const controls = Array.from(xDraggable?.storage.controls.children);

        controls.forEach(setControlCallback);
        setExistingHotspot(id);

        setInitClick(true);
      }
    }
  };

  useEffect(() => {
    const dragmap = draggables.current.map((drag) => drag.id);
    if (status && type === "new") {
      hotspots?.forEach((ht) => {
        if (!dragmap.includes(ht.id) && ht.status === HOTSPOT_STATUS.DRAFT) {
          const xDraggable = subjx(`.hotspot-${ht.id}`).drag(svgParameters());
          if (xDraggable) {
            xDraggable?.fitControlsToSize();
            draggables.current.push({
              id: ht.id,
              object: xDraggable,
            });
            const controls = Array.from(xDraggable?.storage.controls.children);
            controls.forEach(setControlCallback);
          }
        }
      });

      if (draggables.current.length > 0) {
        draggables?.current?.forEach((draggable) => {
          const { id } = draggable || {};
          draggables.current.forEach((otherDraggable) => {
            if (id !== otherDraggable.id) {
              otherDraggable?.object?.disable();
            }
          });
        });
      }
    }
  }, [hotspotsIds.length, status, type, existingHotspot]);

  const iconProps = {
    className: classes.icon,
    htmlColor: "#fff",
  };

  const onImgLoad = ({ target: img }) => {
    const { clientHeight, clientWidth } = img;

    const imgData = { clientHeight, clientWidth };

    setImage(imgData);
  };

  const mapDataMarkup =
    mapPreview || open ? (
      <Box className={classes.mapCanvas}>
        <img
          onLoad={(e) => onImgLoad(e)}
          className={classes.map}
          src={getMediaLink({ link: mapPreview || mapUrl })}
          alt="MapHotspot"
          width={image.clientWidth}
          height={image.clientHeight}
        />
        )
        <svg
          id="svgCanvas"
          onClick={disableHotspot}
          width="100%"
          height="100%"
          className={classes.svgCanvas}
          viewBox={`0 0 ${image.clientWidth || 100} ${image.clientHeight || 100}`}
          preserveAspectRatio="xMinYMin meet"
        >
          <image
            className={classes.realMap}
            href={getMediaLink({ link: mapPreview || mapUrl })}
            alt="MapHotspot"
            width={image.clientWidth}
            height={image.clientHeight}
          />

          {image &&
            hotspots?.map((hotspot) => {
              const { transform } = hotspot?.positionData || {};

              const { a, b, c, d, e, f } = transform || {};
              const isDraggable = currentHotspot ? currentHotspot?.id === hotspot.id : undefined;

              const transformData = transform?.a ? `matrix(${a},${b}, ${c}, ${d}, ${e}, ${f})` : "none";

              const { linkedContent } = hotspot || {};
              // TODO: tooltip implement
              const tooltipMarkup = (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                  }}
                >
                  {renderTypeIcon(linkedContent?.type, iconProps)}
                  {linkedContent?.name || "No Content Linked"}
                </Box>
              );
              return (
                <Tooltip
                  key={hotspot.id}
                  classes={{
                    arrow: classes.arrow,
                    tooltip: classes.tooltip,
                  }}
                  title={tooltipMarkup}
                  placement="left"
                  arrow
                >
                  <rect
                    width={60}
                    height={60}
                    x={image.clientWidth / 2 || 960}
                    y={image.clientHeight / 2 || 540}
                    id={hotspot.id}
                    key={hotspot.id}
                    stroke="#000"
                    strokeWidth={2}
                    strokeDasharray={currentHotspot?.id === hotspot.id ? "10 10" : "none"}
                    vectorEffect="non-scaling-stroke"
                    onClick={(ev) => type !== "new" && activateHotspot(ev, ev.currentTarget.id, linkedContent)}
                    transform={transformData}
                    style={isDraggable ? { cursor: "grab" } : {}}
                    className={`${classes.hotspot} ${hoveredHotspot === hotspot.id && classes.activeHover} ${currentHotspot?.id === hotspot.id && classes.active} hotspot-${hotspot.id}`}
                  />
                </Tooltip>
              );
            })}
        </svg>
      </Box>
    ) : null;

  return (
    <Dialog
      aria-labelledby="simple-modal-title"
      aria-describedby="simple-modal-description"
      maxWidth={false}
      open={!!mapPreview || !!open}
      onClose={handleClose}
      fullScreen={fullScreenSmall}
      disableEnforceFocus={!!mapPreview || open}
      hideBackdrop
      className={classes.dialogBackground}
    >
      <DialogContent className={classes.paper}>
        <div className={classes.mediaPreview}>
          <div className={classes.popupClose}>
            <IconButton size="small" className={classes.closeModalButton} onClick={handleClose}>
              <Close />
            </IconButton>
          </div>
          {mapDataMarkup}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function areEqual(prevProps, nextProps) {
  const difference = objectDifference(nextProps, prevProps);
  return isEmpty(difference);
}

export default memo(MapHotspotCanvas, areEqual);
